import type { Observable } from "rxjs";
import { of, isObservable, from } from "rxjs";
import { catchError } from "rxjs/operators";
import type { IResponse, IResponseWithLoading } from "App/Modules/Models";
import { extendWithLoadingAndFail } from "App/Modules/Services";

export type Response<TResponse> = Observable<IResponse<TResponse>> | Promise<IResponse<TResponse>> | boolean;

/** Transforms various response callbacks to an Observable stream of IResponseWithLoading<TResponse>
 *
 *  errors in original stream or promise will be transformed to IResponseWithLoading with hasError: true
 */
export const transformResponse: <TResponse>(
    submitResult: Response<TResponse>,
) => Observable<IResponseWithLoading<TResponse>> = <TResponse>(submitResult: Response<TResponse>) => {
    if (typeof submitResult === "boolean") {
        return of({
            isLoading: false,
            isSuccessful: submitResult,
            hasError: !submitResult,
        } as IResponseWithLoading<TResponse>);
    }
    if (isObservable<IResponse<TResponse>>(submitResult)) {
        return extendWithLoadingAndFail(submitResult).pipe(
            catchError((error) =>
                of({
                    hasError: true,
                    isLoading: false,
                    isSuccessful: false,
                    ...error,
                } as IResponseWithLoading<TResponse>),
            ),
        );
    }
    if (submitResult.then) {
        return extendWithLoadingAndFail(from(submitResult)).pipe(
            catchError((error) =>
                of({
                    hasError: true,
                    isLoading: false,
                    isSuccessful: false,
                    ...error,
                } as IResponseWithLoading<any>),
            ),
        );
    }
    throw new Error("Unknown return type in onSubmit callback");
};
